export type Environment = 'staging' | 'production';
export const ENVIRONMENTS: Environment[] = ['production', 'staging'];

export type User = { id: string; email: string; display_name: string };
export type App = { id: string; name: string; slug: string; description: string | null; url: string | null; logo: string | null; created_at: string; updated_at: string };
export type PlatformCreds = { enabled: boolean; fingerprint: string | null; verified_at: string | null };
export type AppEnvironment = {
  id: string; environment: Environment; is_active: boolean;
  android: (PlatformCreds & { fcm_project_id: string | null }) | null;
  ios: (PlatformCreds & { key_id: string | null; team_id_apple: string | null; bundle_id: string | null }) | null;
  updated_at: string;
};
export type AppDetail = App & { environments: AppEnvironment[] };
export type ApiKey = { id: string; kind: 'sdk' | 'server'; environment: Environment; name: string; key_prefix: string; key: string | null; last_used_at: string | null; revoked_at: string | null; created_at: string };
export type NotificationPermission = 'not_determined' | 'denied' | 'provisional' | 'authorized';
export type Installation = {
  id: string; installation_id: string; environment: Environment; platform: 'android' | 'ios'; token_preview: string;
  external_user_id: string | null; tags: Record<string, string>; app_version: string | null; os_version: string | null;
  sdk_version: string | null; timezone: string | null; language: string | null; deactivated_at: string | null;
  device_model: string | null; notification_permission: NotificationPermission | null; apns_sandbox: boolean | null; is_test_device: boolean; test_device_name: string | null; is_active: boolean; deactivation_reason: string | null; consecutive_failures: number;
  last_seen_at: string; created_at: string;
};
export type Send = {
  id: string; environment: Environment; source: 'server' | 'admin_test' | 'admin_broadcast'; status: 'queued' | 'accepted' | 'failed';
  title: string; body: string; external_user_id: string | null; target_count: number; platforms: string[];
  accepted_count: number; failed_count: number; click_count: number; error_code: string | null;
  idempotency_key: string | null; correlation_id: string | null; created_at: string; completed_at: string | null;
  /** The message this send belongs to; its own id unless it is a later batch (P5.28). */
  group_id: string;
};
/** One row of Notification History: a send, or the batches of a bigger message added up (P5.28). */
export type Message = Send & { batch_count: number };
export type SendDetail = Message & { outcomes_truncated: boolean; outcomes: Array<{ time: string; event_type: string; platform: string | null; error_code: string | null; installation_id: string | null }> };
export type Page<T> = { data: T[]; next: string | null };
export type SdkRelease = {
  id: string; package: 'server-sdk'; version: string; file_name: string; sha256: string; size_bytes: number;
  git_commit: string | null; release_notes: string | null; built_at: string; created_at: string;
};

/** The environments this app actually has, in display order (production first). */
export const existingEnvironments = (app: Pick<AppDetail, 'environments'>): Environment[] =>
  ENVIRONMENTS.filter((e) => app.environments.some((x) => x.environment === e && x.is_active !== false));

/**
 * The environment to show: the one in the URL if the app has it, otherwise its first existing one. Only an app with
 * no environments at all keeps the requested one (or production), so its pages can offer to create it.
 */
export function resolveEnvironment(app: Pick<AppDetail, 'environments'> | undefined, requested: string | null): Environment {
  const asked = ENVIRONMENTS.includes(requested as Environment) ? (requested as Environment) : null;
  if (!app) return asked ?? 'production';
  const existing = existingEnvironments(app);
  return asked && existing.includes(asked) ? asked : existing[0] ?? asked ?? 'production';
}
